import type { fabric } from "fabric"
import Calc from "@/common/util/calculator"
import BaseFixmethod from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"
import PathObject from "@/signomatic/app/objects/path"
import GroupObject from "../objects/group"
import pathRounding from "@/signomatic/util/path_rounding"

export default class EyeletsFixmethod extends BaseFixmethod {
  private get param() {
    const fixGap = Calc.mmToPx(12.8)
    const alignGap = Calc.mmToPx(26.5)

    return {
      // minimum gap between eyelets
      countGap: Calc.mmToPx(1000),
      // preview eyelet diameter
      diameter: Calc.mmToPx(24),
      // https://github.com/skyltmax/skyltmax/wiki/Eyelet-specs
      distance: Calc.mmToPx(9.5), // L1
      alignGap, // L2
      fixGap, // L4
      fixDiameter: Calc.mmToPx(9.4), // H4 diameter
      alignDiameter: Calc.mmToPx(7), // H1 diameter
      diagonalFixDistance: Math.sqrt(Math.pow(fixGap, 2) / 2), // Corner: α
      diagonalAlignDistance: Math.sqrt(Math.pow(alignGap, 2) / 2), // Middle: α
    }
  }

  get count() {
    return this.positions().length
  }

  topDrawable() {
    const items: fabric.Object[] = []

    for (const e of this.positions()) {
      this.drawShape(items, e.left, e.top, e.direction)
      this.drawEyelet(items, e.left, e.top, e.direction)
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: this.state.widthPx,
        height: this.state.heightPx,
      }),
    ]
  }

  private positions() {
    const { widthPx, heightPx } = this.state
    const pos = []

    const left = widthPx / 2 + this.param.distance - this.param.alignDiameter / 2
    const top = heightPx / 2 + this.param.distance - this.param.alignDiameter / 2
    const bottom = heightPx * 1.5 - this.param.distance - this.param.alignDiameter / 2
    const right = widthPx * 1.5 - this.param.distance - this.param.alignDiameter / 2

    // NW,NE,SW,SE
    pos.push({ left, top, direction: "NW" })
    pos.push({ left, top: bottom, direction: "SW" })
    pos.push({ left: right, top, direction: "NE" })
    pos.push({ left: right, top: bottom, direction: "SE" })

    // W, E
    let items = Math.floor(heightPx / (this.param.countGap + 1))
    let gap = heightPx / (items + 1)
    for (let i = 0; i < items; i++) {
      const center = top + (gap * (i + 1) - this.param.distance - this.param.alignDiameter / 2)
      pos.push({ left, top: center, direction: "W" })
      pos.push({ left: right, top: center, direction: "E" })
    }

    // N, S
    items = Math.floor(widthPx / (this.param.countGap + 1))
    gap = widthPx / (items + 1)
    for (let i = 0; i < items; i++) {
      const center = left + (gap * (i + 1) - this.param.distance - this.param.alignDiameter / 2)
      pos.push({ left: center, top: bottom, direction: "S" })
      pos.push({ left: center, top, direction: "N" })
    }

    return pos
  }

  private drawEyelet(items: fabric.Object[], left: number, top: number, direction: string) {
    this.drawAlignmentHole(items, left, top)

    if (direction.length === 1) {
      this.drawEyeletMiddle(items, left, top, direction)
    } else {
      this.drawEyeletCorner(items, left, top, direction)
    }
  }

  private drawEyeletMiddle(items: fabric.Object[], left: number, top: number, direction: string) {
    let holeLeft = 0
    let holeTop = 0

    if (direction === "N") {
      this.drawAlignmentHole(items, left - this.param.diagonalAlignDistance, top + this.param.diagonalAlignDistance)
      this.drawAlignmentHole(items, left + this.param.diagonalAlignDistance, top + this.param.diagonalAlignDistance)
    } else if (direction === "S") {
      this.drawAlignmentHole(items, left + this.param.diagonalAlignDistance, top - this.param.diagonalAlignDistance)
      this.drawAlignmentHole(items, left - this.param.diagonalAlignDistance, top - this.param.diagonalAlignDistance)
    } else if (direction === "W") {
      this.drawAlignmentHole(items, left + this.param.diagonalAlignDistance, top - this.param.diagonalAlignDistance)
      this.drawAlignmentHole(items, left + this.param.diagonalAlignDistance, top + this.param.diagonalAlignDistance)
    } else if (direction === "E") {
      this.drawAlignmentHole(items, left - this.param.diagonalAlignDistance, top - this.param.diagonalAlignDistance)
      this.drawAlignmentHole(items, left - this.param.diagonalAlignDistance, top + this.param.diagonalAlignDistance)
    }

    if (direction === "N") {
      holeLeft = this.param.alignDiameter / 2 - this.param.fixDiameter / 2
      holeTop = this.param.alignDiameter / 2 + this.param.fixGap - this.param.fixDiameter / 2
    } else if (direction === "S") {
      holeLeft = this.param.alignDiameter / 2 - this.param.fixDiameter / 2
      holeTop = this.param.alignDiameter / 2 - this.param.fixGap - this.param.fixDiameter / 2
    } else if (direction === "W") {
      holeLeft = this.param.alignDiameter / 2 + this.param.fixGap - this.param.fixDiameter / 2
      holeTop = this.param.alignDiameter / 2 - this.param.fixDiameter / 2
    } else if (direction === "E") {
      holeLeft = this.param.alignDiameter / 2 - this.param.fixGap - this.param.fixDiameter / 2
      holeTop = this.param.alignDiameter / 2 - this.param.fixDiameter / 2
    }

    this.drawFixHole(items, left + holeLeft, top + holeTop)
  }

  private drawEyeletCorner(items: any[], left: number, top: number, direction: string) {
    let holeLeft = 0
    let holeTop = 0

    if (direction === "NW" || direction === "NE") {
      this.drawAlignmentHole(items, left, top + this.param.alignGap)
    }
    if (direction === "SW" || direction === "SE") {
      this.drawAlignmentHole(items, left, top - this.param.alignGap)
    }
    if (direction === "NW" || direction === "SW") {
      this.drawAlignmentHole(items, left + this.param.alignGap, top)
    }
    if (direction === "NE" || direction === "SE") {
      this.drawAlignmentHole(items, left - this.param.alignGap, top)
    }

    if (direction === "NW" || direction === "NE") {
      holeTop = this.param.alignDiameter / 2 + this.param.diagonalFixDistance - this.param.fixDiameter / 2
    }
    if (direction === "SW" || direction === "SE") {
      holeTop = this.param.alignDiameter / 2 - this.param.diagonalFixDistance - this.param.fixDiameter / 2
    }
    if (direction === "NW" || direction === "SW") {
      holeLeft = this.param.alignDiameter / 2 + this.param.diagonalFixDistance - this.param.fixDiameter / 2
    }
    if (direction === "NE" || direction === "SE") {
      holeLeft = this.param.alignDiameter / 2 - this.param.diagonalFixDistance - this.param.fixDiameter / 2
    }

    this.drawFixHole(items, left + holeLeft, top + holeTop)
  }

  private drawAlignmentHole(items: fabric.Object[], left: number, top: number) {
    items.push(
      new CircleObject({
        left,
        top,
        strokeWidth: 0,
        radius: this.param.alignDiameter / 2,
        fill: "transparent",
      }).addClass("eyelethole")
    )

    items.push(
      new CircleObject({
        left,
        top,
        stroke: "#bbb",
        strokeWidth: 2,
        radius: this.param.alignDiameter / 2,
        fill: "#ffffff",
      })
    )
  }

  private drawFixHole(items: fabric.Object[], left: number, top: number) {
    items.push(
      new CircleObject({
        left,
        top,
        strokeWidth: 0,
        radius: this.param.fixDiameter / 2,
        fill: "transparent",
      }).addClass("eyelethole")
    )

    items.push(
      new CircleObject({
        left,
        top,
        stroke: "#cccccc",
        strokeWidth: 1,
        radius: this.param.fixDiameter / 2,
        fill: this.state.color,
      })
    )
  }

  private drawShape(items: fabric.Object[], left: number, top: number, direction: string) {
    const points = []
    const offset = 5
    const width = 200
    const height = 200
    const params = {
      stroke: "#cccccc",
      strokeWidth: 1,
      fill: "#ffffff",
      angle: 0,
    }
    let x = 0
    let y = 0

    if (direction === "NW") {
      x = left - this.param.distance + this.param.alignDiameter / 2 + offset
      y = top - this.param.distance + this.param.alignDiameter / 2 + offset
      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x, y: y + height })
    }
    if (direction === "NE") {
      x = left + this.param.alignDiameter / 2 + this.param.distance - width - offset
      y = top - this.param.distance + this.param.alignDiameter / 2 + offset
      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x: x + width, y: y + height })
    }
    if (direction === "SW") {
      x = left - this.param.distance + this.param.alignDiameter / 2 + offset
      y = top + this.param.alignDiameter / 2 + this.param.distance - height - offset
      points.push({ x, y })
      points.push({ x: x + width, y: y + height })
      points.push({ x, y: y + height })
    }
    if (direction === "SE") {
      x = left + this.param.alignDiameter / 2 + this.param.distance - width - offset
      y = top + this.param.alignDiameter / 2 + this.param.distance - height - offset
      points.push({ x: x + width, y })
      points.push({ x: x + width, y: y + height })
      points.push({ x, y: y + height })
    }
    if (direction === "N") {
      x = left + this.param.alignDiameter / 2
      y = top - this.param.distance + this.param.alignDiameter / 2 + offset
      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x, y: y + height })
      params.angle = 45
    }
    if (direction === "S") {
      x = left + this.param.alignDiameter / 2
      y = top + this.param.alignDiameter / 2 + this.param.distance - offset
      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x, y: y + height })
      params.angle = 225
    }
    if (direction === "W") {
      x = left - this.param.distance + this.param.alignDiameter / 2 + offset
      y = top + this.param.alignDiameter / 2
      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x, y: y + height })
      params.angle = -45
    }
    if (direction === "E") {
      x = left + this.param.alignDiameter / 2 + this.param.distance - offset
      y = top + this.param.alignDiameter / 2
      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x, y: y + height })
      params.angle = 135
    }

    const pathString = [
      "M",
      points[0].x,
      points[0].y,
      "L",
      points[1].x,
      points[1].y,
      "L",
      points[2].x,
      points[2].y,
      "Z",
    ].join(" ")

    const path = pathRounding(pathString, 40).join("")
    items.push(new PathObject(path, params))
  }
}
